<template>
  <v-card class="candidate edit qualification">
    <v-toolbar
      v-if="qualification.id"
      @click="show = !show"
      style="cursor: pointer"
      class="subheading"
      color="white"
      height="70"
      flat
    >
      <v-toolbar-side-icon class="pl-2">
        <v-avatar v-if="qualification.institution" size="50">
          <img
            :src="
              qualification.institution.logo ||
              '/img/missing/logos/institutions/original.png'
            "
          />
        </v-avatar>
      </v-toolbar-side-icon>
      <v-toolbar-title>
        <div class="pt-2">
          <div v-if="qualification.qualification">
            {{ qualification.qualification.display }}
          </div>
          <div
            v-if="qualification.institution"
            :class="themes.candidate.light.mutedText"
            class="caption"
          >
            {{ qualification.institution.name }}
          </div>
        </div>
      </v-toolbar-title>
      <v-spacer />
      <Flag
        v-for="(f, i) in flags"
        :key="`qualification-flag-${i}`"
        :value="f"
        icon
      />
      <v-tooltip left v-if="qualification.verifiedAt">
        <img
          class="verified"
          src="/img/verified.png"
          width="24"
          height="24"
          slot="activator"
        />
        <span>
          Verified by Leaply on
          {{ new Date(qualification.verifiedAt) | date("d MMMM yyy") }}
        </span>
      </v-tooltip>
      <v-icon>
        {{ show ? "mdi-chevron-up" : "mdi-chevron-down" }}
      </v-icon>
    </v-toolbar>

    <v-slide-y-transition>
      <div v-if="show">
        <v-form v-model="valid">
          <v-card-text>
            <InstitutionSelect
              @input="setInstitutionFilter"
              :value="filter.institution"
              :disabled="!!(modified.institution && modified.institution.id)"
              required
              :toFront="false"
            />

            <QualificationSelect
              :disabled="!filter.institution || !filter.institution.id"
              :color="themes.candidate.light.input"
              :institutions="[filter.institution]"
              :value="modified.qualification"
              :rules="rules.qualification"
              @input="setQualification"
              :toFront="false"
              immediate
              required
            >
              <template slot="selection" slot-scope="{ qualification }">
                <div class="py-2">
                  <div>{{ qualification.display }}</div>
                </div>
              </template>
            </QualificationSelect>

            <QualificationDatesSelect
              v-if="modified"
              :requestGraduationDate="requestGraduationDate"
              v-model="modified"
              :hideStartsAt="hideStartsAt"
              :hideEndsAt="hideEndsAt"
            />

            <GradeSelect
              v-if="
                modified.startsAt && new Date(modified.startsAt) < new Date()
              "
              :color="themes.candidate.light.input"
              :value="modified"
              :toFront="false"
              :scales="desiredGradeScales"
              @input="setGrade"
            />
            <v-alert
              v-else-if="modified.startsAt"
              :color="themes.candidate.light.muted"
              :value="true"
              class="mt-3"
              outline
              type="info"
            >
              Intended studies don't require grades or academic results
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-menu
              :close-on-content-click="false"
              nudge-top="30"
              left
              v-if="modified.id"
            >
              <v-btn
                fab
                flat
                slot="activator"
                :color="themes.candidate.light.danger"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-card toolbar>
                <v-toolbar card :color="themes.candidate.light.dialogTitle">
                  {{
                    severeFlags
                      ? "This qualification has been flagged and cannot be removed."
                      : "Are you sure?"
                  }}
                  <v-spacer />
                  <v-btn
                    v-if="severeFlags"
                    @click="sendCrispMessage"
                    :color="themes.candidate.light.warning"
                    dark
                  >
                    Request removal
                  </v-btn>
                  <v-btn
                    v-else
                    @click="deleteQualification"
                    :color="themes.candidate.light.danger"
                    :loading="saving"
                    dark
                  >
                    Delete
                  </v-btn>
                </v-toolbar>
              </v-card>
            </v-menu>

            <v-btn :color="themes.candidate.light.cancel" @click="cancel" flat>
              Cancel
            </v-btn>
            <v-dialog v-model="confirming" width="400">
              <v-card>
                <v-card-title class="headline"> Are you sure? </v-card-title>
                <v-card-text>
                  This qualification has been
                  <em>Verified by Leaply</em>. Editing it will cause it to no
                  longer show as Verified to employers until it can be reviewed
                  by Leaply again.
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-btn
                    flat
                    :color="themes.candidate.light.cancel"
                    @click="confirming = false"
                  >
                    cancel
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    :color="themes.candidate.light.primaryActionButton"
                    @click="submit"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-btn
              :color="themes.candidate.light.primaryActionButton"
              :disabled="!changes || !valid || saving"
              :loading="saving"
              @click="qualification.verifiedAt ? (confirming = true) : submit()"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>

        <div v-if="showComponentsSection && qualification.id">
          <v-divider />
          <v-toolbar
            @click="showComponents = !showComponents"
            class="subheading transparent"
            style="cursor: pointer"
            flat
          >
            <v-toolbar-side-icon>
              <v-icon :color="themes.candidate.light.muted">
                mdi-book-open-page-variant</v-icon
              >
            </v-toolbar-side-icon>
            <v-toolbar-title>Add or edit subjects and modules</v-toolbar-title>
            <v-spacer />
            <v-icon>
              {{ showComponents ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </v-toolbar>
          <v-card-text v-show="showComponents">
            <QualificationComponents :qualification="qualification" />
          </v-card-text>
        </div>
      </div>
    </v-slide-y-transition>
  </v-card>
</template>

<script>
// Components
import Flag from "@/components/candidate/flags/Flag";
import GradeSelect from "@/components/selects/GradeSelect";
import InstitutionSelect from "@/components/selects/InstitutionSelect";
import QualificationComponents from "@/components/candidate/profile/edit/qualifications/QualificationComponents";
import QualificationSelect from "@/components/selects/QualificationSelect";
import QualificationDatesSelect from "@/components/selects/QualificationDatesSelect";

// Helpers
import { byGradeValue } from "@/sorting/QualificationComponents";
import _ from "lodash";

// Data
import gradeScales from "@/data/grade_scales";

export default {
  components: {
    Flag,
    GradeSelect,
    InstitutionSelect,
    QualificationComponents,
    QualificationDatesSelect,
    QualificationSelect,
  },

  created() {
    if (_.get(this, "qualification.institution")) {
      this.filter.institution = this.qualification.institution;
    }
  },

  props: {
    flags: { type: Array, default: () => [] },
    hideEndsAt: { type: Boolean, default: false },
    hideStartsAt: { type: Boolean, default: false },
    profile: { type: Object, required: true },
    qualification: { type: Object, required: true },
    requestGraduationDate: { type: Boolean, default: true },
    requiresProofOfRegistration: { type: Boolean, default: () => false },
    requiresQualificationTranscript: { type: Boolean, default: () => false },
    withComponents: { type: Boolean, default: true },
  },

  data() {
    return {
      show: !this.qualification.id,
      saving: false,
      confirming: false,
      showComponents: false,
      modified: JSON.parse(JSON.stringify(this.qualification)),
      filter: { institution: {} },
      valid: false,
      rules: { qualification: [(v) => (!!v && !!v.id) || "must be provided"] },
      lockGradeScale: false,
    };
  },

  computed: {
    changes() {
      if (!_.get(this.qualification, "id")) return true;

      return !_.isEqual(this.modified, this.qualification);
    },

    logo() {
      return _.get(this.qualification, "institution.logo");
    },

    hasDisciplines() {
      return (
        _.get(this, "qualification.disciplines[0]") ||
        _.get(this, "modified.qualification.disciplines[0]")
      );
    },

    isSecondary() {
      return (
        _.get(
          this,
          "qualification.qualificationType.qualificationCategory.name"
        ) === "Secondary Education" ||
        _.get(this, "modified.qualification.qualificationCategory.name") ===
          "Secondary Education"
      );
    },

    showComponentsSection() {
      const hasComponents = _.get(
        this,
        "qualification.profileQualificationComponents[0]"
      );
      const needsDisciplines = !this.hasDisciplines;

      return (
        this.withComponents &&
        (hasComponents || this.isSecondary || needsDisciplines)
      );
    },

    desiredGradeScales() {
      if (this.lockGradeScale) {
        return [this.modified.qualification.institution.gradeScale];
      } else {
        return gradeScales;
      }
    },

    severeFlags() {
      return _.some(this.flags, "isSevere");
    },
  },

  methods: {
    cancel() {
      if (!_.get(this.qualification, "id") && this.changes)
        this.$emit("refresh");

      this.modified = JSON.parse(JSON.stringify(this.qualification));
      this.show = false;
      this.confirming = false;
    },

    deleteQualification() {
      this.saving = true;
      this.$store
        .dispatch("candidate/profileQualifications/destroy", this.modified.id)
        .then(() => {
          this.$store.dispatch("snackbarSuccess", "Qualification removed");
          this.$emit("refresh");
          this.saving = false;
        });
    },

    setGrade({ gradeScale, gradeValue }) {
      if (gradeScale && gradeValue) {
        this.modified.gradeScale = gradeScale;
        this.modified.gradeValue = gradeValue;
      } else {
        this.modified.gradeScale = null;
        this.modified.gradeValue = null;
      }
    },

    setInstitutionFilter(institution) {
      Object.assign(this.filter, { institution: {} });

      if (_.get(institution, "id")) {
        this.filter.institution = JSON.parse(JSON.stringify(institution));
      }
    },

    setQualification(qualification) {
      Object.assign(this.modified, {
        institution: {},
        institutionName: "",
        qualification: {},
        qualificationType: {},
        qualificationTypeName: "",
        specialization: "",
      });

      // Set Institution
      if (_.get(qualification, "institution.name")) {
        this.modified.institution = JSON.parse(
          JSON.stringify(qualification.institution)
        );
        this.modified.institutionName = `${qualification.institution.name}`;
      }

      // Set gradeScale
      if (
        _.get(qualification, "institution.gradeScale") &&
        !this.modified.gradeValue
      ) {
        this.modified.gradeScale = qualification.institution.gradeScale;
        this.lockGradeScale = true;
      } else {
        this.lockGradeScale = false;
      }

      // Set Type
      if (_.get(qualification, "qualificationType.name")) {
        this.modified.qualificationType = JSON.parse(
          JSON.stringify(qualification.qualificationType)
        );
        this.modified.qualificationTypeName = `${qualification.qualificationType.name}`;
        this.modified.specialization = `${qualification.specialization}`;
        this.modified.qualification = JSON.parse(JSON.stringify(qualification));
      }
    },

    submit() {
      this.saving = true;
      this.confirming = false;
      if (this.modified.id) {
        // Update
        this.$store
          .dispatch("candidate/profileQualifications/update", this.modified)
          .then(() => {
            this.$store.dispatch("snackbarSuccess", "Qualification updated");
            this.$emit("refresh");
            this.show = false;
          })
          .finally(() => (this.saving = false));
      } else {
        // Create
        this.$store
          .dispatch("candidate/profileQualifications/create", this.modified)
          .then(() => {
            this.$store.dispatch("snackbarSuccess", "Qualification added");
            this.$emit("refresh");
          })
          .finally(() => (this.saving = false));
      }
    },

    sendCrispMessage(e) {
      if (!window.CRISP_READY) return;
      $crisp.push(["do", "chat:open"]);
      $crisp.push([
        "do",
        "message:send",
        [
          "text",
          `Hi! I would like to remove my qualification "${this.qualification.qualification.display} from ${this.qualification.institution.name}", but it has been flagged. Can you help me?`,
        ],
      ]);
    },
  },
};
</script>

<style>
img.verified {
  margin: 4px 4px 0 0;
}
</style>
